import { Link, useLocation } from 'react-router-dom'
import { C } from '../lib/landingColors'
import { MARKETING_HTML_LANG, type MarketingLocale } from '../lib/marketingLocale'
import { findMarketingPair, marketingLocaleOfPath } from '../lib/marketingPages'

const ORDER: MarketingLocale[] = ['en', 'de']
const SHORT: Record<MarketingLocale, string> = { en: 'EN', de: 'DE' }
/** Endonyms — a reader looking for their language recognises it in that language. */
const FULL: Record<MarketingLocale, string> = { en: 'English', de: 'Deutsch' }

/**
 * DE/EN switch for the marketing documents.
 *
 * Deliberately the dumbest possible implementation: a plain `<Link>` to the
 * sibling URL of the page you are on. No state, no i18n, no side effects —
 * switching the language of a public page must NEVER call
 * `i18n.changeLanguage()` or touch the locale in localStorage, because the
 * browser reading /press may also be logged in as a studio member, and their
 * admin `uiLocale` is not marketing's to overwrite.
 *
 * It derives the pair from the current pathname rather than from a prop, so it
 * works identically on a comparison page (whose two URLs are different slugs,
 * `/compare/…` and `/vergleich/…`) and on a `/de`-prefixed document. Pages that
 * are a single URL are not in the pair registry, and there the switch renders
 * nothing rather than offering a link to a page that does not exist.
 *
 * The emphasis is on the SELECTED language, which sounds obvious and was
 * backwards until 2026-08-19: the current language was plain body text while
 * the other one was accent-coloured and underlined, so the only item carrying
 * any visual weight was the one you were NOT reading. Underline and colour are
 * link affordances, and a two-item switch has no other cue to go on — readers
 * parsed the pair exactly inverted. Now the current language is the solid pill
 * and the sibling is quiet until you point at it.
 */
export function MarketingLanguageSwitch() {
  const { pathname } = useLocation()
  const pair = findMarketingPair(pathname)
  const current = marketingLocaleOfPath(pathname)
  if (!pair || !current) return null

  return (
    <nav aria-label={current === 'de' ? 'Sprache' : 'Language'}
      className="flex items-center justify-end gap-1 text-xs font-medium mb-6">
      {ORDER.map((locale, i) => (
        <span key={locale} className="flex items-center gap-1">
          {i > 0 && <span aria-hidden="true" style={{ color: C.border }}>·</span>}
          {locale === current ? (
            <span aria-current="true" className="px-2 py-0.5 rounded font-semibold"
              style={{ color: C.text, background: C.surface, border: `1px solid ${C.border}` }}>
              {SHORT[locale]}
            </span>
          ) : (
            <Link
              to={pair[locale]}
              hrefLang={MARKETING_HTML_LANG[locale]}
              lang={MARKETING_HTML_LANG[locale]}
              aria-label={FULL[locale]}
              className="px-2 py-0.5 rounded no-underline hover:underline underline-offset-4 transition-colors"
              style={{ color: C.muted }}
              onMouseEnter={(e) => (e.currentTarget.style.color = C.text)}
              onMouseLeave={(e) => (e.currentTarget.style.color = C.muted)}
            >
              {SHORT[locale]}
            </Link>
          )}
        </span>
      ))}
    </nav>
  )
}
